import { Component, OnInit, Input } from '@angular/core';
import { Operator } from 'src/app/planner/enums/Operator.enum';
import { IdNameVO } from 'src/app/planner/interfaces/vo/IdNameVO';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { BrowseFilterPipe } from './browse-filter.pipe';

export interface mappedResponse {
  results: IdNameVO[],
  hasMore: boolean
}

export interface BIESettings {
  placeholder?: string;
  url?: string;
  param?: any;
  column?: BIEColumn;
  fetchData?: (params: object) => Promise<any>;
  responseMapper?: (response: any, offset: number, limit: number) => mappedResponse;
  exclude?: boolean;
  idField?: string;
  showOperator?: boolean;
}

export interface BIEColumn {
  dataKey?: string;
  accessor?: (row: any) => string;
  subtextAccessor?: (row: any) => string;
}

@Component({
  selector: 'browse-include-exclude',
  templateUrl: './browse-include-exclude.component.html',
  styleUrls: ['./browse-include-exclude.component.scss'],
  providers: [
    BrowseFilterPipe
  ]
})

export class BrowseIncludeExcludeComponent implements OnInit {

  searchString = new FormControl();
  _settings: BIESettings;
  defaultSettings: BIESettings = {
    placeholder: 'Search',
    param: {
      offset: 0,
      limit: 25
    },
    column: {
      dataKey: 'name',
      accessor: (row: any) => {
        return row.name
      }
    },
    exclude: true,
    idField: 'id',
    showOperator: true
  }
  items: any[] = [];
  remoteDataSearch: boolean = true;
  _dataConfig: any;

  @Input('hidden') hidden: boolean = false;

  @Input()
  get dataConfig() {
    return this._dataConfig;
  }
  set dataConfig(values) {
    this._dataConfig = values;
    this.updatePreSelectionData(this._dataConfig);
  }

  @Input() public set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.searchString.disable();
    } else {
      this.searchString.enable();
    }
  }

  @Input()
  public set settings(value: BIESettings) {
    if (value) {
      this._settings = Object.assign(this.defaultSettings, value);
    } else {
      this._settings = Object.assign(this.defaultSettings);
    }
  }

  @Input()
  public set data(value: Array<any>) {
    if (value) {
      this.items = value;
      this._dupItems = value;
      this.remoteDataSearch = false;
    }
  }

  _dupItems = [];
  _disabled: boolean = false;
  searchStringValue: string = '';
  offset: number = 0;
  hasMore: boolean = false;
  incRadioSelected = Operator.or;
  excRadioSelected = Operator.or;
  _showExclude: boolean = true;
  includeOp: any;
  excludeOp: any;
  radioSelected: any;
  selectedIncludeItems: Array<any> = [];
  selectedExcludeItems: Array<any> = [];
  includeItemSelected: boolean;
  excludeItemSelected: boolean;
  operatorEnum = Operator;
  loading: boolean = false;

  constructor(
    private http: HttpClient,
    private browseFilterPipe: BrowseFilterPipe
  ) { }

  ngOnInit() {
    this.fetchList();
    this.searchString.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.searchStringValue = value;
      if (!this.remoteDataSearch) {
        this.items = this.browseFilterPipe.transform(this._dupItems, this.searchStringValue);
        return;
      }
      this.offset = 0;
      this.items = [];
      this.fetchList();
    });
  }

  updatePreSelectionData(dataVO) {
    if (dataVO.include) {
      this.selectedIncludeItems = dataVO.include;
    }
    if (dataVO.exclude) {
      this.selectedExcludeItems = dataVO.exclude;
    }
    this.syncDataItems();
    if (this.searchString.value && this.searchString.value.length > 0) {
      this.searchString.patchValue('');
    }
  }

  syncDataItems() {
    _.forEach(this.items, (item) => {
      item.isInclude = this.selectedIncludeItems.some((v) => v[this._settings.idField] === item[this._settings.idField]);
      if (this._settings.exclude) {
        item.isExclude = this.selectedExcludeItems.some((v) => v[this._settings.idField] === item[this._settings.idField]);
      }
    });
  }

  defaultResponseMapper(response: any, offset: number, limit: number): mappedResponse {
    let count: number = response.count || response.totalNum || response.results.length + offset + 1;
    let hasMore = (count > offset + limit) || response.hasMore;
    return {
      results: response.results,
      hasMore: !!hasMore
    }
  }

  handleOnScroll() {
    if (this.hasMore) {
      this.offset += this._settings.param.limit;
      this.fetchList();
    }
  }

  fetchList() {
    if (!this.remoteDataSearch) {
      return;
    }
    let params = Object.assign(this._settings.param, {
      searchString: this.searchStringValue,
      offset: this.offset.toString()
    });

    let promise: Promise<any>;
    if (this._settings.url) {
      this.loading = true;
      promise = this.http.get(this._settings.url, { params: params }).toPromise();
    } else {
      promise = this._settings.fetchData(params);
    }

    promise.then((response: any) => {
      let mappedResponse: mappedResponse;
      if (this._settings.responseMapper) {
        mappedResponse = this._settings.responseMapper(response, this.offset, this._settings.param.limit);
      } else {
        mappedResponse = this.defaultResponseMapper(response, this.offset, this._settings.param.limit);
      }
      if (this.offset == 0) {
        this.items = mappedResponse.results;
      } else {
        this.items = this.items.concat(mappedResponse.results);
      }
      this.syncDataItems();
      this.hasMore = mappedResponse.hasMore;
      this.loading = false;
    }).catch((err) => {
      if (this.offset == 0) {
        this.items = [];
        this.loading = false;
      }
      this.hasMore = false;
    });
  }

  includeItem(item) {
    if (item.isInclude) {
      this.removeSelectedIncludes(item);
    } else if (!item.isExclude) {
      item.isInclude = true;
      this.selectedIncludeItems.push(item);
    }
  }

  excludeItem(item) {
    if (item.isExclude) {
      this.removeSelectedExcludes(item);
    } else if (!item.isInclude) {
      item.isExclude = true;
      this.selectedExcludeItems.push(item);
    }
  }

  removeSelectedExcludes(item) {
    item.isExclude = false;
    let removeSelectedExcludeItem = this.selectedExcludeItems.find((v) => v[this._settings.idField] === item[this._settings.idField]);
    _.remove(this.selectedExcludeItems, removeSelectedExcludeItem);
    this.syncDataItems();
  }

  removeSelectedIncludes(item) {
    item.isInclude = false;
    let removeSelectedIncludeItem = this.selectedIncludeItems.find((v) => v[this._settings.idField] === item[this._settings.idField]);
    _.remove(this.selectedIncludeItems, removeSelectedIncludeItem);
    this.syncDataItems();
  }

  resetIncludes() {
    this.selectedIncludeItems.length = 0;
    this.syncDataItems();
  }

  resetExcludes() {
    this.selectedExcludeItems.length = 0;
    this.syncDataItems();
  }

  resetAll() {
    this.selectedIncludeItems.length = 0;
    this.selectedExcludeItems.length = 0;
    this.syncDataItems();
  }

}